/**
 * Earthsea Compendium PWA shell.
 *
 * Reads one bundle and does everything client-side, so search and browsing work
 * offline. No framework: the app is a router, a search index and five views, and
 * a runtime dependency would outweigh all of it. Types come from ../shared, which
 * the bundle builder also uses, so a change to the bundle shape fails to compile
 * rather than failing to render.
 *
 * Built with esbuild to public/app.js. Do not edit that file.
 */

import type { Bundle, BundleEntry, Citation, DictionaryWord, LinkRender, VersionInfo } from "../shared";
import { initThemeToggle } from "./theme";

initThemeToggle();

interface IndexRow {
  slug: string;
  name: string;
  /** Name, aliases, titles and stripped prose, lowercased, for substring search. */
  haystack: string;
  /** Just the names, for ranking a name hit above a body hit. */
  names: string;
  /** Deduplicated words from names/aliases/titles only, for the fuzzy fallback.
   *  Scoped to proper nouns, not the prose body: fuzzy-matching against hundreds
   *  of body words per entry turns "dragon" into a near-miss for "dragoon", the
   *  Old Speech gloss two rows down, and every other six-letter word in Earthsea. */
  nameWords: string[];
}

const state: {
  bundle: Bundle | null;
  bySlug: Map<string, BundleEntry>;
  index: IndexRow[];
  filter: string | null;
  hash: string | null;
} = { bundle: null, bySlug: new Map(), index: [], filter: null, hash: null };

type Child = Node | string | number | null | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> = {},
  ...kids: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const node = Object.assign(document.createElement(tag), props);
  for (const kid of kids.flat()) {
    if (kid != null) node.append(kid instanceof Node ? kid : document.createTextNode(String(kid)));
  }
  return node;
}

const main = () => document.getElementById("main")!;

function say(message: string, sticky = false): void {
  const bar = document.getElementById("status")!;
  bar.textContent = message;
  bar.hidden = !message;
  if (message && !sticky) setTimeout(() => { bar.hidden = true; }, 4000);
}

// ------------------------------------------------------------------- loading

async function loadBundle(): Promise<void> {
  const version = await fetch("/api/version")
    .then((r) => (r.ok ? (r.json() as Promise<VersionInfo>) : null))
    .catch(() => null);

  if (!version?.url) {
    // Offline, or no bundle built yet. A cached bundle beats an error page.
    const cached = await caches.match(new Request("/api/version"));
    if (!cached) throw new Error("no bundle available");
    const stale = (await cached.json()) as VersionInfo;
    return loadFrom(stale.url, stale.hash);
  }

  document.getElementById("version")!.textContent =
    `${version.entry_count} entries, built ${new Date(version.built_at + "Z").toLocaleDateString()}`;
  return loadFrom(version.url, version.hash);
}

async function loadFrom(url: string, hash?: string, allowRetry = true): Promise<void> {
  const res = await fetch(url);

  // The bundle ships as a static asset, and a deploy replaces the whole asset set,
  // so a hash held by a returning client can stop resolving. That is expected, not
  // exceptional: ask what the current hash is and take that instead.
  if (res.status === 404 && allowRetry) {
    const current = await fetch("/api/version", { cache: "reload" })
      .then((r) => (r.ok ? (r.json() as Promise<VersionInfo>) : null))
      .catch(() => null);
    if (current?.url && current.url !== url) {
      return loadFrom(current.url, current.hash, false);
    }
  }

  if (!res.ok) throw new Error(`bundle fetch failed: ${res.status}`);
  adopt((await res.json()) as Bundle, hash);
}

function adopt(bundle: Bundle, hash?: string): void {
  state.bundle = bundle;
  state.hash = hash ?? state.hash;
  state.bySlug = new Map(bundle.entries.map((e) => [e.slug, e]));
  // One flat haystack per entry, built once. 1280 entries is small enough that a
  // linear scan beats shipping an index structure.
  state.index = bundle.entries.map((e) => {
    const names = [e.name, ...e.aliases, ...e.titles].join(" ").toLowerCase();
    return {
      slug: e.slug,
      name: e.name,
      haystack: [e.name, ...e.aliases, ...e.titles, e.description.replace(/<[^>]+>/g, " ")]
        .join(" ").toLowerCase(),
      names,
      nameWords: [...new Set(names.split(/[^a-z0-9']+/).filter(Boolean))],
    };
  });
}

// -------------------------------------------------------------------- search

/** Iterative edit distance. Only ever called on single words, so the O(n*m) table is tiny. */
function levenshtein(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    let prev = dp[0];
    dp[0] = j;
    for (let i = 1; i <= a.length; i++) {
      const tmp = dp[i];
      dp[i] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[i], dp[i - 1]);
      prev = tmp;
    }
  }
  return dp[a.length];
}

/** Typo tolerance for a search term: 1 stray letter up to length 4, 2 beyond that. */
function fuzzyThreshold(term: string): number {
  return term.length <= 4 ? 1 : 2;
}

/** True if `term` is a near-miss (not an exact substring) for one of the entry's
 *  own names/aliases/titles. Never checked against prose, see nameWords above. */
function fuzzyHit(item: IndexRow, term: string): boolean {
  if (term.length < 3) return false; // too short for edit distance to mean anything
  const threshold = fuzzyThreshold(term);
  return item.nameWords.some((w) => Math.abs(w.length - term.length) <= threshold
    && levenshtein(term, w) <= threshold);
}

function search(query: string): { total: number; hits: IndexRow[]; terms: string[] } {
  const q = query.trim().toLowerCase();
  const terms = q ? q.split(/\s+/) : [];
  if (!terms.length) return { total: 0, hits: [], terms };
  const scored: (IndexRow & { score: number })[] = [];
  for (const item of state.index) {
    let fuzzy = false;
    const matchesAll = terms.every((t) => {
      if (item.haystack.includes(t)) return true;
      const hit = fuzzyHit(item, t);
      if (hit) fuzzy = true;
      return hit;
    });
    if (!matchesAll) continue;
    // Rank: exact name, then name prefix, then a name hit, then body only. A fuzzy
    // term match always ranks below every exact match, at any of those levels.
    const score = (item.names === q ? 0 : item.names.startsWith(q) ? 1
      : item.names.includes(q) ? 2 : 3) + (fuzzy ? 10 : 0);
    scored.push({ ...item, score });
  }
  scored.sort((a, b) => a.score - b.score || a.name.localeCompare(b.name));
  // The cap is on what is rendered, not on what is counted, so the total shown to
  // the reader is the real one.
  return { total: scored.length, hits: scored.slice(0, 100), terms };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Wraps exact (non-fuzzy) term hits in <mark>. Typo matches aren't highlightable. */
function highlightName(name: string, terms: string[]): Child[] {
  if (!terms.length) return [name];
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi");
  // Match/non-match alternates by index in split()'s output; filtering the empty
  // strings split() leaves at the edges *before* that parity check would shift
  // every index after them, marking the wrong word.
  return name.split(pattern)
    .map((part, i) => (i % 2 === 1 ? el("mark", { textContent: part }) : part))
    .filter((part) => part !== "");
}

// --------------------------------------------------------------------- views

const TYPES = ["People", "Places", "Objects", "Abstract", "Flora & fauna"];

/**
 * The glossary (this site's home view) is the one page every visitor lands on,
 * and until this list existed the other pages were reachable only as single
 * words buried inside About's prose ("80 timeline events"), easy to read past
 * entirely. About and Links stay in the header nav; these are the rest, with
 * real descriptions. Labels match the page titles set in D1 (which replaced the
 * legacy site-wide title, once identical across all twelve pages).
 */
const OTHER_PAGES: { slug: string; label: string; blurb: string }[] = [
  { slug: "timeline", label: "Timeline", blurb: "Events of Earthsea history, in approximate order" },
  { slug: "factfile", label: "Factfiles", blurb: "Books, maps and the Masters of Roke" },
  { slug: "character", label: "Character profiles", blurb: "In-depth profiles of major characters, in progress" },
  { slug: "ged", label: "On Ged's names", blurb: "The meanings behind his true name and use-names" },
  { slug: "roke_masters", label: "Masters of Roke", blurb: "Every Archmage and Master since the school's founding" },
  { slug: "maps", label: "Maps", blurb: "Online maps of the Archipelago" },
  { slug: "source", label: "Source editions", blurb: "The six books this compendium draws from" },
  { slug: "essay", label: "Essays", blurb: "Essays and articles about Earthsea (coming soon)" },
  { slug: "new", label: "Updates", blurb: "A history of changes to this site" },
  { slug: "earthsea", label: "Original site introduction", blurb: "The legacy site's front page, preserved as-is" },
];

/** Fills the header's "More" menu. Every page is one click away from anywhere,
 *  instead of a card grid that pushed the glossary itself below the fold. */
function initMoreMenu(): void {
  const known = new Set(state.bundle!.pages.map((p) => p.slug));
  document.getElementById("more-list")!.replaceChildren(
    ...OTHER_PAGES.filter((p) => known.has(p.slug)).map((p) =>
      el("li", {}, el("a", { href: `/page/${p.slug}` },
        el("strong", { textContent: p.label }), el("span", { textContent: p.blurb })))));
}

/** A visible way back from any entry or page; the brand link is easy to miss. */
function backLink(): HTMLElement {
  return el("a", { className: "back", href: "/", textContent: "\u2190 All entries" });
}

function renderList(
  entries: BundleEntry[], heading: string, note?: string, highlightTerms: string[] = []
): void {
  const out: Node[] = [el("h2", { textContent: heading })];
  if (note) out.push(el("p", { className: "meta", textContent: note }));

  out.push(el("ul", { className: "filters" }, TYPES.map((type) =>
    el("li", {}, el("button", {
      type: "button",
      textContent: type,
      ariaPressed: String(state.filter === type),
      onclick: () => { state.filter = state.filter === type ? null : type; route(); },
    })))));

  // One group per initial: the big letter is sticky within its own group, so it
  // stays beside its names as they scroll past. Search results arrive ranked, not
  // alphabetical, so they stay a single ungrouped run.
  const list = el("ul", { className: "entry-list" });
  let letter: string | null = null;
  let names: HTMLUListElement | null = null;
  for (const entry of entries) {
    if (!highlightTerms.length) {
      const initial = (entry.sort_name || entry.name)[0].toUpperCase();
      if (initial !== letter) {
        letter = initial;
        names = el("ul", { className: "names" });
        list.append(el("li", { className: "letter-group" },
          el("span", { className: "letter", ariaHidden: "true", textContent: letter }), names));
      }
    } else if (!names) {
      names = el("ul", { className: "names" });
      list.append(el("li", { className: "letter-group" }, el("span"), names));
    }
    const link = el("a", { className: "name", href: `/entry/${entry.slug}` }, highlightName(entry.name, highlightTerms));
    names!.append(entry.redirect_to
      ? el("li", { className: "redirect" }, el("div", { className: "row" }, link,
          el("span", { className: "see" }, "see ", el("a", {
            href: `/entry/${entry.redirect_to}`,
            textContent: state.bySlug.get(entry.redirect_to)?.name ?? entry.redirect_to,
          }))))
      : el("li", {}, el("div", { className: "row" }, link,
          entry.type ? el("span", { className: "tag", textContent: entry.type }) : null)));
  }
  // A-Z strip: scrollIntoView, not #hash links, because the global click handler
  // would turn a hash link into a route change and drop the fragment.
  if (!highlightTerms.length) {
    const groups = [...list.querySelectorAll<HTMLElement>(".letter-group")];
    out.push(el("nav", { className: "jump", ariaLabel: "Jump to letter" },
      groups.map((g) => {
        const l = g.querySelector(".letter")!.textContent!;
        return el("button", { type: "button", textContent: l, onclick: () => g.scrollIntoView({ behavior: "smooth", block: "start" }) });
      })));
  }
  out.push(list);
  main().replaceChildren(...out);
}

function citation(item: Citation): string {
  const book = state.bundle!.books.find((b) => b.abbrev === item.book);
  return [item.chapter, book ? book.title : item.book].filter(Boolean).join(", ");
}

/** Live links are links. Dead links keep the label and show where they pointed. */
function renderLink(link: LinkRender): HTMLLIElement {
  if (link.as_hyperlink) {
    return el("li", {}, el("a", { href: link.original_url, rel: "noopener noreferrer" },
      link.label || link.original_url));
  }
  return el("li", { className: "dead-link" },
    el("span", { textContent: link.label || "Untitled link" }), " ",
    el("span", { className: "tag", textContent: "link no longer working" }),
    el("br"),
    el("span", { className: "url", textContent: link.show_url_as_text ?? link.original_url }));
}

function renderEntry(slug: string): void {
  const entry = state.bySlug.get(slug);
  if (!entry) {
    main().replaceChildren(el("h2", { textContent: "Not found" }),
      el("p", {}, "No entry called ", el("code", { textContent: slug }), "."));
    return;
  }
  if (entry.redirect_to) {
    // Stubs stay addressable in their own right, then send the reader on.
    navigate(`/entry/${entry.redirect_to}`, true);
    say(`"${entry.name}" is listed under ${state.bySlug.get(entry.redirect_to)?.name}`);
    return;
  }

  const head = el("header", { className: "entry-head" });
  if (entry.type) head.append(el("span", { className: "tag", textContent: entry.type }));
  head.append(el("h2", { textContent: entry.name }));

  // Names and cross-references go in a side rail, so the prose gets a readable
  // measure and the facts a reader scans for are always in the same place.
  const rail = el("aside", { className: "rail" });
  const fields = el("dl", { className: "fields" });
  if (entry.aliases.length) {
    fields.append(el("dt", { textContent: "Also known as" }),
      el("dd", { textContent: entry.aliases.join(", ") }));
  }
  if (entry.titles.length) {
    fields.append(el("dt", { textContent: "Titles" }),
      el("dd", { textContent: entry.titles.join(", ") }));
  }
  if (fields.childElementCount) rail.append(fields);
  if (entry.related.length) {
    rail.append(el("h3", { textContent: "See also" }),
      el("ul", {}, entry.related.map((r) =>
        el("li", {}, el("a", { href: `/entry/${r}`, textContent: state.bySlug.get(r)?.name ?? r })))));
  }

  const body = el("div", { className: "entry-body" });
  // Sanitised at build time in bundle.ts, which is why innerHTML is acceptable
  // here. Do not move that step into the client.
  const prose = el("div", { className: "prose" });
  prose.innerHTML = entry.description;
  body.append(prose);

  for (const quote of entry.quotes) {
    body.append(el("blockquote", {}, el("p", { textContent: quote.text }),
      el("cite", { textContent: citation(quote) })));
  }

  if (entry.sources.length) {
    body.append(el("h3", { textContent: "Sources" }),
      el("ul", { className: "sources" }, entry.sources.map((s) =>
        el("li", {}, citation(s), s.footnote ? ` (${s.footnote})` : null))));
  }

  if (entry.links.length) {
    body.append(el("h3", { textContent: "External links" }),
      el("ul", { className: "links" }, entry.links.map(renderLink)));
  }

  const out: Node[] = [backLink(), head, el("div", { className: rail.childElementCount ? "entry-grid" : "entry-grid solo" },
    rail.childElementCount ? rail : null, body)];

  document.title = `${entry.name} | An Earthsea Compendium`;
  main().replaceChildren(...out);
}

function renderPage(slug: string): void {
  const page = state.bundle!.pages.find((p) => p.slug === slug);
  if (!page) {
    main().replaceChildren(el("h2", { textContent: "Not found" }),
      el("p", {}, "No page called ", el("code", { textContent: slug }), "."));
    return;
  }
  const body = el("div", { className: "prose legacy-page" });
  body.innerHTML = page.body_html;   // sanitised at build time
  document.title = `${page.title} | An Earthsea Compendium`;
  // A genuine data table (border="1": Roke Masters, the timeline) keeps its row/
  // column structure, per app.css, rather than being flattened like the border="0"
  // layout tables. That means it can be wider than a phone screen, so it scrolls
  // horizontally in its own box instead of overflowing the page.
  for (const table of body.querySelectorAll('table[border="1"]')) {
    const wrapper = el("div", { className: "table-scroll" });
    table.replaceWith(wrapper);
    wrapper.append(table);
  }
  // No separate links list: the page body carries its own anchors, already
  // rendered live or dead by the bundle builder.
  main().replaceChildren(backLink(), body);
}

function renderDictionary(): void {
  const byLanguage = new Map<string, DictionaryWord[]>();
  for (const word of state.bundle!.dictionary) {
    byLanguage.set(word.language, [...(byLanguage.get(word.language) ?? []), word]);
  }
  const out: Node[] = [
    backLink(),
    el("h2", { textContent: "Dictionary" }),
    el("p", { className: "meta",
      textContent: "Words of the Old Speech and other languages of Earthsea" }),
  ];
  for (const [language, words] of byLanguage) {
    out.push(el("h3", { textContent: language }),
      el("dl", { className: "fields" }, words.flatMap((w) => [
        el("dt", { textContent: w.headword }),
        el("dd", { textContent: w.gloss }),
      ])));
  }
  main().replaceChildren(...out);
}

// -------------------------------------------------------------------- router

function navigate(path: string, replace = false): void {
  history[replace ? "replaceState" : "pushState"]({}, "", path);
  // A replace happens inside route() itself (redirect stubs), where a second
  // transition would cancel the first, so only real navigations cross-fade.
  if (!replace && "startViewTransition" in document
      && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document.startViewTransition(() => { route(); window.scrollTo(0, 0); });
  } else {
    route();
    if (!replace) window.scrollTo(0, 0);
  }
}

function route(): void {
  if (!state.bundle) return;
  const { pathname, searchParams } = new URL(location.href);
  document.title = "An Earthsea Compendium";
  // The giant title is the glossary's own; everywhere else the hero shrinks to a
  // search strip (app.css keys off this).
  const wasHome = document.body.dataset.view === "home";
  document.body.dataset.view = pathname === "/" ? "home" : "inner";
  // Chrome stops sampling SMIL motion inside a display:none subtree and does not
  // resume when it is shown again (the clock runs, the hawk stays put). Restarting
  // the SVG clock wakes it, and the journey starts again from Gont.
  if (pathname === "/" && !wasHome) document.querySelector<SVGSVGElement>(".atlas svg")?.setCurrentTime(0);
  document.getElementById("more")?.removeAttribute("open");

  const entry = /^\/entry\/([\w-]+)$/.exec(pathname);
  if (entry) return renderEntry(entry[1]);

  const page = /^\/page\/([\w-]+)$/.exec(pathname);
  if (page) return renderPage(page[1]);

  if (pathname === "/dictionary") return renderDictionary();

  if (pathname === "/search") {
    const q = searchParams.get("q") ?? "";
    (document.getElementById("q") as HTMLInputElement).value = q;
    const { total, hits, terms } = search(q);
    const shown = total > hits.length ? `, showing the first ${hits.length}` : "";
    return renderList(
      hits.map((h) => state.bySlug.get(h.slug)!),
      "Search",
      `${total} ${total === 1 ? "match" : "matches"} for “${q}”${shown}`,
      terms
    );
  }

  const entries = state.bundle.entries.filter((e) => !state.filter || e.type === state.filter);
  // Redirect stubs are listed but are not entries in their own right, and the
  // legacy site's own totals exclude them, so they are counted separately.
  const stubs = entries.filter((e) => e.redirect_to).length;
  renderList(entries, "Glossary",
    `${entries.length - stubs} entries${state.filter ? ` in ${state.filter}` : ""}` +
    (stubs ? ` plus ${stubs} cross-references` : ""));
}

document.addEventListener("click", (event) => {
  const link = (event.target as HTMLElement).closest("a");
  if (!link || link.origin !== location.origin || link.hasAttribute("download")) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  navigate(link.pathname + link.search);
});

addEventListener("popstate", route);

document.getElementById("search-form")!.addEventListener("submit", (event) => {
  event.preventDefault();
  clearTimeout(searchDebounce);
  const q = (document.getElementById("q") as HTMLInputElement).value.trim();
  navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/");
});

// Live results while typing. Debounced against the fuzzy fallback's per-keystroke
// scan cost, and using replaceState (not navigate/pushState) so backspacing through
// a query doesn't fill the back button with one history entry per letter typed.
let searchDebounce: ReturnType<typeof setTimeout> | undefined;
document.getElementById("q")!.addEventListener("input", () => {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    const q = (document.getElementById("q") as HTMLInputElement).value.trim();
    const target = q ? `/search?q=${encodeURIComponent(q)}` : "/";
    if (location.pathname + location.search === target) return;
    history.replaceState({}, "", target);
    route();
  }, 150);
});

// -------------------------------------------------------------- service worker

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => say("Offline support unavailable"));
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    if (event.data?.type === "bundle-ready") {
      say("A newer version of the compendium is ready. Reload to use it.", true);
    }
  });
}

loadBundle()
  .then(() => {
    initMoreMenu();
    route();
    // Ask the worker to precache the current bundle for offline use.
    navigator.serviceWorker?.ready.then((reg) =>
      reg.active?.postMessage({ type: "prefetch-bundle", hash: state.hash }));
  })
  .catch((err: unknown) => {
    main().replaceChildren(
      el("h2", { textContent: "The compendium could not be loaded" }),
      el("p", { textContent: err instanceof Error ? err.message : String(err) }),
      el("p", {}, "If you are offline and have not visited before, there is nothing cached yet."));
  });
